import React, { useEffect } from "react";
import DashboardContainer from "../../components/dashboard-container";
import Chatbot from "@yoctotta/chatbot";
import "@yoctotta/chatbot/dist/chatbot.css";
import { Box, Typography, useTheme } from "@mui/material";
import { kamanService } from "../../utils/services/kamanService";
import { agentConfigService } from "../../utils/services/agentConfigService";
import  config  from "../../config";

const Agent = ({}) => {
  const [canAccess, setCanAccess] = React.useState(false);
  const [kamanToken, setKamanToken] = React.useState("");
  const theme = useTheme();
  const defaultColor = "#D13438";
  const primary_color = theme?.palette?.primary?.main || defaultColor;
  const secondary_color = theme?.palette?.secondary?.main || defaultColor;
  
  const id = window.location.pathname.split("/").pop();
  const [agentDetails, setAgentDetails] = React.useState(null);
  const [agentNotFound, setAgentNotFound] = React.useState(false);

  useEffect(() => {
    setAgentNotFound(false);
    setAgentDetails(null);
    agentConfigService
      .fetch(null, `id=${id}`)
      .then((response) => {
        if (response.total && response.data?.length) {
          setAgentDetails(response.data[0]);
        } else {
          setAgentNotFound(true);
        }
      })
      .catch((error) => {
        console.error("Error fetching agent config:", error);
        setAgentNotFound(true);
      });
  }, [id]);

  useEffect(() => {
    const fetchKamanToken = async () => {
      try {
        const response = await kamanService.generateToken();
        if (response && response.token) {
          setKamanToken(response.token);
          setCanAccess(true);
        }
      } catch (error) {
        console.error("Error fetching Kaman token:", error);
      }
    };

    fetchKamanToken();
  }, []);

  return (
    <DashboardContainer
      filterButton={false}
      hideUploadButton
      hideAddButton
      hideFilterClient={true}
      hideAllButton
      hideGridList
    >
      {/* Overlay container with relative positioning */}
      <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
        {agentNotFound && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "calc(100vh - 164px)",
              width: "100%",
              px: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                p: { xs: 3, md: 5 },
                borderRadius: 3,
                border: "1px solid var(--border-color)",
                backgroundColor: "var(--bg-paper)",
                boxShadow: "0 4px 20px rgba(0,0,0,0.06)",
                maxWidth: 480,
                width: "100%",
              }}
            >
              <Box
                sx={{
                  width: 180,
                  height: 180,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 2,
                }}
              >
                <svg
                  viewBox="0 0 240 240"
                  width="100%"
                  height="100%"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <defs>
                    <linearGradient
                      id="agentBotGrad"
                      x1="0"
                      y1="0"
                      x2="1"
                      y2="1"
                    >
                      <stop offset="0%" stopColor={primary_color} />
                      <stop offset="100%" stopColor={secondary_color} />
                    </linearGradient>
                  </defs>
                  {/* Antenna */}
                  <line
                    x1="120"
                    y1="30"
                    x2="120"
                    y2="55"
                    stroke="var(--text-tertiary)"
                    strokeWidth="3"
                    strokeLinecap="round"
                  />
                  <circle cx="120" cy="26" r="6" fill={primary_color} />
                  {/* Head */}
                  <rect
                    x="55"
                    y="55"
                    width="130"
                    height="105"
                    rx="22"
                    fill="url(#agentBotGrad)"
                    opacity="0.9"
                  />
                  {/* Face panel */}
                  <rect
                    x="68"
                    y="72"
                    width="104"
                    height="70"
                    rx="14"
                    fill="var(--bg-paper)"
                    opacity="0.95"
                  />
                  {/* Eyes (sad / closed) */}
                  <path
                    d="M88 108 q8 -10 16 0"
                    stroke="var(--text-tertiary)"
                    strokeWidth="4"
                    fill="none"
                    strokeLinecap="round"
                  />
                  <path
                    d="M136 108 q8 -10 16 0"
                    stroke="var(--text-tertiary)"
                    strokeWidth="4"
                    fill="none"
                    strokeLinecap="round"
                  />
                  {/* Mouth */}
                  <path
                    d="M104 130 q16 -10 32 0"
                    stroke="var(--text-tertiary)"
                    strokeWidth="3"
                    fill="none"
                    strokeLinecap="round"
                  />
                  {/* Side ears */}
                  <rect
                    x="45"
                    y="92"
                    width="12"
                    height="30"
                    rx="4"
                    fill={primary_color}
                    opacity="0.7"
                  />
                  <rect
                    x="183"
                    y="92"
                    width="12"
                    height="30"
                    rx="4"
                    fill={primary_color}
                    opacity="0.7"
                  />
                  {/* Body / base */}
                  <rect
                    x="80"
                    y="168"
                    width="80"
                    height="30"
                    rx="10"
                    fill="var(--border-color)"
                    opacity="0.6"
                  />
                  {/* Warning badge */}
                  <circle
                    cx="178"
                    cy="62"
                    r="20"
                    fill="#FFB020"
                    stroke="var(--bg-paper)"
                    strokeWidth="3"
                  />
                  <text
                    x="178"
                    y="69"
                    textAnchor="middle"
                    fontSize="22"
                    fontWeight="700"
                    fill="#fff"
                    fontFamily="Poppins, sans-serif"
                  >
                    !
                  </text>
                </svg>
              </Box>
              <Typography
                sx={{
                  fontFamily: "Poppins",
                  fontSize: 20,
                  fontWeight: 600,
                  color: "var(--text-tertiary)",
                  mb: 0.5,
                }}
              >
                Agent Not Configured
              </Typography>
              <Typography
                sx={{
                  fontFamily: "Poppins",
                  fontSize: 14,
                  fontWeight: 400,
                  color: "var(--text-secondary)",
                  mb: 1.5,
                }}
              >
                We couldn't find an agent for the requested ID. Please configure
                one to start chatting.
              </Typography>
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1,
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 1,
                  backgroundColor: "var(--bg-input)",
                  border: "1px dashed var(--border-color)",
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "Poppins",
                    fontSize: 12,
                    color: "var(--text-secondary)",
                  }}
                >
                  Agent ID:
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "Poppins",
                    fontSize: 12,
                    fontWeight: 600,
                    color: "var(--text-tertiary)",
                  }}
                >
                  {id}
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
        {kamanToken && agentDetails && (
          <Box sx={{ filter: canAccess ? "none" : "blur(2px)" }}>
            <Chatbot
              baseUrl={config.kaman_baseUrl}
              token={kamanToken}
              expertId={id}
              theme={{
                primaryColor: primary_color, //"#c5c7c9ff",
                secondaryColor: secondary_color, //"#dadce2ff",
                textColor: "var(--text-primary)",
                backgroundColor: "var(--bg-primary)",
                borderColor: "var(--border-color)",
                borderRadius: "8px",
                fontFamily: "Poppins",
                // fontSize: "12px",
              }}
              showAppsSelection={false}
              style={{
                width: "100%",
                height: "calc(100vh - 164px)", // Adjust height based on your layout
                textAlign: "left",
              }}
              expertName={agentDetails?.name}
            />
          </Box>
        )}

      </Box>
    </DashboardContainer>
  );
};

export default Agent;
